import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { dimenticaQuestoTelefono, registraNotifiche } from "@/lib/push";

export interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  company: string;
  city: string;
  province: string;
  brands: string[];
  is_admin: boolean;
  /** La postazione del banco Datacom: vede e risponde a tutte le chat. */
  banco_operatore?: boolean;
  /** Il privilegio della chat col banco: l'admin puo' toglierlo dal pannello. */
  banco_abilitato?: boolean;
  /** Inizio della prova dei 7 giorni. */
  created_at?: string;
  /** Fine dell'abbonamento assegnato da Datacom; null = senza scadenza. */
  scadenza?: string | null;
  /**
   * La partita IVA e' il cancello dal 28/08: senza, l'app non si usa.
   * Chi si iscrive dal modulo la scrive li'; chi entra con Google o Apple non
   * compila niente, quindi arriva qui vuota e la chiede /completa-profilo.
   */
  partita_iva?: string | null;
  telefono?: string | null;
  /** valida | sconosciuta | non_valida. Lo scrive solo la edge function. */
  piva_esito?: string | null;
  /**
   * Account di prova di un costruttore (25/09): lo crea Datacom dal pannello,
   * a tempo e con i soli marchi scelti, senza partita IVA. Colonna riservata.
   */
  account_prova?: boolean;
  /**
   * Consenso alle offerte via email (02/11/2026): true si', false no, null mai
   * chiesto (l'app lo chiede una volta), undefined prima della migrazione.
   */
  consenso_marketing?: boolean | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  emailVerified: boolean;
  isAdmin: boolean;
  /**
   * La scheda viene dal database in questa apertura, non dalla memoria del
   * telefono. Quella ricordata puo' avere settimane: Eurolab (05/11/2026) ha
   * riaperto l'app Android dopo venti giorni, la scheda ricordata era della
   * prova finita e il popup «la prova e' finita» e' partito su un account con
   * la licenza attiva da otto giorni.
   */
  profiloVerificato: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Il telefono RICORDA la scheda dell'utente (dal 23/09/2026).
 *
 * All'avvio la rotellina aspettava la riga di `profiles` dal database, senza
 * limite di tempo: con 4G ballerino l'app restava ferma su uno spinner scuro
 * per decine di secondi — l'«apertura lenta» dell'iPhone. La sessione invece
 * e' gia' sul telefono e torna in un attimo.
 *
 * Ora l'ultima scheda vista si salva accanto alla sessione e all'avvio si usa
 * SUBITO; la riga fresca arriva un secondo dopo e la sostituisce. Se Datacom
 * nel frattempo ha tolto un marchio o chiuso l'account, la porta vera e' la
 * funzione `chat`, che legge il database a ogni domanda: qui si decide solo
 * cosa mostrare nel primo secondo. Cambio di persona o uscita = memoria pulita.
 */
const CHIAVE_PROFILO = "datacom.profilo.";
/** Oltre questo tempo la rotellina si ferma e si entra con quello che c'e'. */
const TETTO_ATTESA_PROFILO_MS = 8000;

function leggiProfiloRicordato(uid: string): Profile | null {
  try {
    const grezzo = localStorage.getItem(CHIAVE_PROFILO + uid);
    if (!grezzo) return null;
    const p = JSON.parse(grezzo) as Profile;
    return p && p.id === uid ? p : null;
  } catch {
    return null;
  }
}

function ricordaProfilo(uid: string, p: Profile | null) {
  try {
    if (p) localStorage.setItem(CHIAVE_PROFILO + uid, JSON.stringify(p));
    else localStorage.removeItem(CHIAVE_PROFILO + uid);
  } catch {
    /* senza memoria locale si vive lo stesso: si aspetta il database */
  }
}

function dimenticaProfili() {
  try {
    const via: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CHIAVE_PROFILO)) via.push(k);
    }
    via.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* localStorage non disponibile */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  // Di chi e' la scheda letta dal database in questa apertura (profiloVerificato).
  const [profiloLettoPer, setProfiloLettoPer] = useState<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const qc = useQueryClient();

  const fetchProfile = useCallback(async (userId: string) => {
    setProfileLoading(true);
    // Un tetto, non un annullamento: allo scadere la rotellina si ferma e si
    // entra con la scheda ricordata (o senza); la risposta, quando arriva,
    // viene applicata lo stesso.
    const tetto = window.setTimeout(() => {
      if (currentUserIdRef.current === userId) setProfileLoading(false);
    }, TETTO_ATTESA_PROFILO_MS);
    const { data, error } = await supabase
      .from("profiles")
      // select(*) e non un elenco: nell'intervallo fra deploy e migrazione la
      // colonna `scadenza` non esiste ancora, e un elenco esplicito farebbe
      // fallire la query lasciando l'utente senza profilo (= senza marchi).
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    window.clearTimeout(tetto);
    // Evita race: solo se è ancora l'utente corrente
    if (currentUserIdRef.current !== userId) return;
    if (error) {
      // Rete assente o database muto: la scheda ricordata resta valida, non
      // la si cancella con un null. Chi non ne ha una entra senza marchi e
      // la funzione `chat` decide da sola.
      setProfileLoading(false);
      return;
    }
    const p = (data as unknown as Profile | null) ?? null;
    setProfile(p);
    ricordaProfilo(userId, p);
    setProfiloLettoPer(userId);
    setProfileLoading(false);
  }, []);

  useEffect(() => {
    // 1) Registra il listener PRIMA di getSession (regola Lovable/Supabase)
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      const uid = newSession?.user?.id ?? null;
      // CAMBIO DI PERSONA = MEMORIA PULITA. Le conversazioni, i messaggi, le
      // ultime domande e i calcoli restavano nella cache di React Query anche
      // dopo l'uscita: sul telefono aziendale o sul PC dell'ufficio, chi
      // entrava dopo vedeva per qualche secondo la roba di chi c'era prima
      // (le "ultime domande" in home restano valide 30 secondi). Sono dati di
      // lavoro di un altro installatore: non devono comparire mai.
      if (uid !== currentUserIdRef.current) {
        qc.clear();
        // La scheda ricordata e' di CHI C'ERA PRIMA: via, se c'era qualcuno.
        // (All'avvio il riferimento e' ancora vuoto: non e' un cambio di
        // persona, e la scheda serve proprio in quel momento.)
        if (currentUserIdRef.current) dimenticaProfili();
      }
      currentUserIdRef.current = uid;
      if (event === "SIGNED_IN") {
        // Migrazione: pulisce vecchie conversazioni salvate in localStorage
        try {
          const toRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k) continue;
            if (
              k.startsWith("datacom.conversations") ||
              k.startsWith("datacom.conversation.") ||
              k.startsWith("datacom.chat.")
            ) {
              toRemove.push(k);
            }
          }
          toRemove.forEach((k) => {
            console.log("[useAuth] migration: removing legacy localStorage key", k);
            localStorage.removeItem(k);
          });
        } catch {
          /* localStorage non disponibile */
        }
      }
      if (!uid) {
        setProfile(null);
        setProfileLoading(false);
        // Uscita (o nessuno dentro): niente scheda sul telefono.
        dimenticaProfili();
      } else {
        // Deferred per evitare deadlock dentro il callback
        setTimeout(() => {
          void fetchProfile(uid);
        }, 0);
      }
    });

    // 2) Sessione iniziale
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      const uid = data.session?.user?.id ?? null;
      currentUserIdRef.current = uid;
      if (uid) {
        // Prima quello che il telefono ricorda (istantaneo), poi il database.
        const ricordato = leggiProfiloRicordato(uid);
        if (ricordato) setProfile(ricordato);
        void fetchProfile(uid);
      }
      setSessionReady(true);
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile, qc]);

  /**
   * Il telefono esce dalla rubrica PRIMA di chiudere la sessione.
   *
   * L'ordine non e' un dettaglio: cancellare la riga richiede di essere ancora
   * autenticati (la policy filtra su auth.uid()). Fatto dopo il signOut, il
   * DELETE non tocca niente e non da' errore - il collega che entra dopo
   * continuerebbe a ricevere le notifiche del precedente.
   */
  const signOut = useCallback(async () => {
    await dimenticaQuestoTelefono();
    await supabase.auth.signOut();
  }, []);

  /**
   * Registrazione alle notifiche appena c'e' un utente con l'email confermata.
   *
   * Qui e non all'avvio: la finestra del permesso si puo' mostrare una volta
   * sola, e chiederla a chi non ha ancora capito cos'e' l'app significa
   * prendersi un "no" definitivo. Sul sito la funzione esce da sola.
   */
  useEffect(() => {
    if (!user?.email_confirmed_at) return;
    void registraNotifiche();
  }, [user?.id, user?.email_confirmed_at]);

  /**
   * La partita IVA si controlla da sola, al primo accesso utile.
   *
   * Chi si iscrive scrive il numero nel modulo, ma il verdetto lo da' il
   * server: qui si chiede solo che lo dia. Parte una volta - quando l'esito e'
   * ancora vuoto - e non blocca niente: se il registro europeo non risponde,
   * l'installatore usa l'app lo stesso e il pannello lo mostra fra quelli da
   * guardare. Far dipendere l'ingresso da un servizio che non e' nostro
   * sarebbe il modo piu' rapido per tenere fuori un cliente vero.
   */
  useEffect(() => {
    if (!profile?.partita_iva) return;
    if (profile.piva_esito) return;
    void supabase.functions
      .invoke("verifica-piva")
      .then(() => fetchProfile(profile.id))
      .catch(() => {});
  }, [profile?.id, profile?.partita_iva, profile?.piva_esito, fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (user?.id) await fetchProfile(user.id);
  }, [user?.id, fetchProfile]);

  const value = useMemo<AuthContextValue>(() => {
    const isAuthenticated = !!user;
    const isLoading =
      !sessionReady || (isAuthenticated && profile === null && profileLoading);
    return {
      user,
      session,
      profile,
      isLoading,
      isAuthenticated,
      emailVerified: !!user?.email_confirmed_at,
      isAdmin: profile?.is_admin === true,
      profiloVerificato: !!user && profiloLettoPer === user.id,
      signOut,
      refreshProfile,
    };
  }, [user, session, profile, sessionReady, profileLoading, profiloLettoPer, signOut, refreshProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Come useAuth, ma senza pretendere il provider: fuori dal provider (o prima del
 * login) restituisce null invece di lanciare. Serve agli strumenti, che devono
 * funzionare anche in uno smoke test renderizzato nudo.
 */
export function useProfiloOpzionale(): Profile | null {
  const ctx = useContext(AuthContext);
  return ctx?.profile ?? null;
}

/** L'utente autenticato, o null: per le pagine raggiungibili anche da fuori.
 * L'email sta QUI e non nel profilo: profiles non ha una colonna email,
 * l'indirizzo vive in auth.users. */
export function useUtenteOpzionale(): User | null {
  const ctx = useContext(AuthContext);
  return ctx?.user ?? null;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}